// Modules:
import React, { useState, useEffect, useContext } from 'react';

// Utils:
import TopicConfigContext from 'utilities/topicConfigContext';

// Components:
import { useErrorHandler } from 'react-error-boundary';
// TODO: Remove 'applyAlternativeAgeLanguage' after full dynamic chart/tables title refactor
import { handleFetchError, applyAlternativeAgeLanguage } from 'utilities/utils';
import VerticalBar from 'components/Charts/VerticalBar';
import TrendLine from '../Charts/TrendLine';
import HorizontalBar from '../Charts/HorizontalBar';
import BarLineMix from '../Charts/BarLineMix';
import ConfidenceIntervalHorizontalBar from '../Charts/ConfidenceIntervalHorizontalBar';

// Styles:
import muiTheme from '../../theme';

// Style:
const styles = {
    '&.MuiFormControl-root': {
        margin: '0 auto',
    },
    '&.MuiAlert-root': {
        '&:hover': {
            cursor: 'pointer',
        },
    },
    '& .MuiMenuItem-root': {
        color: 'red',
    },
    '&.MuiButton-root': {
        borderRight: '1px solid rgb(99, 167, 220) !important',
        minHeight: 'fit-content',
        margin: '0.5em 0',
        width: 'fit-content',
    },
    '&.MuiIconButton-root': {
        margin: 0,
    },
    '&.MuiButtonGroup-root': {
        margin: '0.7rem',
        maxWidth: '85%',
    },
    '&.MuiButton-contained': {
        background: muiTheme.palette.lightBlue.main,
        color: 'white',
        '&:hover': {
            background: muiTheme.palette.secondary.dark,
        },
    },
    '&.MuiButton-outlined': {
        color: muiTheme.palette.lightBlue.main,
        '&:hover': {
            background: muiTheme.palette.lightGray.main,
        },
    },
};

// Functions (do not use props or state):

// Sorts fetched data alphabetically or numerically by axis specified
const sortByAxis = (items, axis, baseline) => {
    // Copy items to avoid mutating state:
    const sortedItems = items.sort((item, nextItem) => {
        if (item[axis] !== baseline && nextItem[axis] === baseline) {
            return 1;
        }
        if (item[axis] === baseline && nextItem[axis] !== baseline) {
            return -1;
        }
        if (item[axis] === nextItem[axis]) {
            return 0;
        }
        return item[axis] > nextItem[axis] ? 1 : -1;
    });
    return sortedItems;
};

const checkIfNull = (datasetArray, filterKey) =>
    datasetArray
        .filter((dataset) => !dataset[filterKey])
        .map((dataset) => dataset.data)
        .flat()
        .every((dataPoint) => dataPoint === null);

const ChartContent = ({
    counties,
    setChartDemographicContent,
    setTableData,
    setMissingSuppressedModalOpen,
    stratification,
    setStratification,
    currentData,
    setCurrentData,
    selection,
    setSelection,
    ageGroups,
    setAgeGroups,
    setChartInfoModalOpen,
    setShowDataUnavailable,
    // TODO: Remove after full dynamic chart/tables title refactor
    useAlternativeTitlesAndLabels,
}) => {
    // // State // //
    const [dataUnavailable, setDataUnavailable] = useState(false);

    // // Context // //
    const topicConfigContext = useContext(TopicConfigContext);

    // // Hooks // //
    const handleError = useErrorHandler();

    // Function to handle dropdown selection for county selector
    const handleDropdownSelect = (e) => {
        setSelection(e.target.value);
    };

    // Function to handle dropdown selection for age group selector
    const handleAgeGroupSelect = (e) => {
        setAgeGroups(e.target.value);
    };

    // // Effects // //
    // Effect to set the initial selections for age groups if the topic is Falls
    useEffect(
        () => {
            if (topicConfigContext.tab.chartSettings.ageSelectable) {
                setAgeGroups(
                    topicConfigContext.tab.chartSettings.baselineSettings
                );
            }
        },
        // Note: We are intentionally not including baselineSettings in the dependency array in order to persist selections across dissimilar tabs
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            topicConfigContext.topic.topicTitle,
            topicConfigContext.tab.chartSettings.url,
            topicConfigContext.tab.chartSettings.defaultSelectionName,
            topicConfigContext.tab.chartSettings.ageSelectable,
            setAgeGroups,
        ]
    );

    // Effect to fetch data when selection changes and update demo profile
    useEffect(() => {
        const getQueryString = (selected) => {
            const queryString = topicConfigContext.tab.chartSettings.urlParams.reduce(
                (
                    urlParamString,
                    currentParamObject,
                    currentIndex,
                    paramsArr
                ) => {
                    const { param } = currentParamObject;

                    // If there is no 'value' key present in
                    // the object, it is the 'jurisdiction' which
                    // updates dynamically by the selector.
                    const value = currentParamObject.value
                        ? currentParamObject.value
                        : selected;

                    const additionalParams =
                        currentIndex + 1 !== paramsArr.length ? '&' : '';

                    return `${urlParamString}${param}=${value}${additionalParams}`;
                },
                ''
            );

            return queryString ? `?${queryString}` : '';
        };

        if (selection) {
            const baseURL = `${
                topicConfigContext.tab.chartSettings.url
            }${getQueryString(selection)}`;

            setChartDemographicContent({ properties: { MDCode: selection } });
            const fetchData = (url) => {
                fetch(url)
                    .then(handleFetchError)
                    .then((res) => res.json())
                    .then((resJson) => {
                        setCurrentData(resJson);
                    })
                    .catch(handleError);
            };

            setDataUnavailable(false);
            fetchData(baseURL);
        }
    }, [
        selection,
        topicConfigContext.tab.chartSettings.url,
        handleError,
        topicConfigContext.tab.chartSettings.urlParams,
        setChartDemographicContent,
        setCurrentData,
    ]);

    // Effect to format table data on data change:
    useEffect(() => {
        if (!currentData || !selection) return;

        const formatTableData = (json) => {
            const {
                tableTitle,
                chartSettings: {
                    defaultSetNames,
                    chartXAxisField,
                    baselineSettings: baseline,
                    infoID,
                    infoTitle,
                    columnHeaders,
                    chartConfigs,
                },
            } = topicConfigContext.tab;

            let headers = [...columnHeaders];

            if (baseline && baseline.includes(selection)) {
                headers = headers.filter(
                    (header) =>
                        defaultSetNames.includes(header.field) ||
                        header.field === chartXAxisField
                );
            } else {
                headers = headers.filter((header) => !header.stratification);
            }

            if (stratification) {
                const stratColumns = columnHeaders.filter(
                    (header) => stratification === header.stratification
                );

                const orderMap = new Map(
                    chartConfigs
                        .filter((set) => set.stratification === stratification)
                        .map((item) => [item.setName, item.order])
                );

                const sortedColumns = stratColumns.slice().sort((a, b) => {
                    const orderA = orderMap.get(a.field) ?? Infinity;
                    const orderB = orderMap.get(b.field) ?? Infinity;
                    return orderA - orderB;
                });

                const stateIndex = headers.findIndex(
                    (item) => item.field === 'stateRate'
                );
                headers.splice(stateIndex, 0, ...sortedColumns);
            }

            const sortedFeatures = sortByAxis(
                [...json],
                chartXAxisField,
                baseline
            );

            setTableData([
                {
                    id: infoID,
                    info: {
                        infoTitle,
                        columnHeaders: headers,
                        // TODO: Remove after full dynamic chart/tables title refactor
                        tableTitle: applyAlternativeAgeLanguage(
                            tableTitle,
                            useAlternativeTitlesAndLabels
                        ),
                    },
                    selection,
                    features: sortedFeatures,
                },
            ]);
        };

        formatTableData(currentData);
    }, [
        currentData,
        selection,
        setTableData,
        stratification,
        topicConfigContext.tab,
        useAlternativeTitlesAndLabels,
    ]);

    // // Render // //
    // eslint-disable-next-line no-nested-ternary
    return topicConfigContext.tab.chartSettings.chartType === 'trendline' ? (
        <TrendLine
            key={topicConfigContext.tab.tabPath} // Note: key is used to force an unmount/mount when switching between buttons which both contain a Trendline component (otherwise cleanup effect will not run)
            counties={counties}
            json={currentData}
            selection={selection}
            stratification={stratification}
            setStratification={setStratification}
            dataUnavailable={dataUnavailable}
            setDataUnavailable={setDataUnavailable}
            styles={styles}
            handleDropdownSelect={handleDropdownSelect}
            ageGroups={ageGroups}
            handleAgeGroupSelect={handleAgeGroupSelect}
            checkIfNull={checkIfNull}
            setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
            setChartInfoModalOpen={setChartInfoModalOpen}
            setShowDataUnavailable={setShowDataUnavailable}
            useAlternativeTitlesAndLabels={useAlternativeTitlesAndLabels}
        />
    ) : // eslint-disable-next-line no-nested-ternary
    topicConfigContext.tab.chartSettings.chartType === 'horizontalBar' ? (
        <HorizontalBar
            json={currentData}
            selection={selection}
            sortByAxis={sortByAxis}
            dataUnavailable={dataUnavailable}
            setDataUnavailable={setDataUnavailable}
            handleDropdownSelect={handleDropdownSelect}
            checkIfNull={checkIfNull}
            styles={styles}
            setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
            setChartInfoModalOpen={setChartInfoModalOpen}
        />
    ) : // eslint-disable-next-line no-nested-ternary
    topicConfigContext.tab.chartSettings.chartType ===
      'confidenceIntervalHorizontalBar' ? (
        <ConfidenceIntervalHorizontalBar
            json={currentData}
            selection={selection}
            stratification={stratification}
            setStratification={setStratification}
            sortByAxis={sortByAxis}
            dataUnavailable={dataUnavailable}
            setDataUnavailable={setDataUnavailable}
            handleDropdownSelect={handleDropdownSelect}
            checkIfNull={checkIfNull}
            styles={styles}
            setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
            setChartInfoModalOpen={setChartInfoModalOpen}
        />
    ) : // eslint-disable-next-line no-nested-ternary
    topicConfigContext.tab.chartSettings.chartType === 'mixed' ? (
        <BarLineMix
            counties={counties}
            json={currentData}
            sortByAxis={sortByAxis}
            selection={selection}
            handleDropdownSelect={handleDropdownSelect}
            dataUnavailable={dataUnavailable}
            setDataUnavailable={setDataUnavailable}
            checkIfNull={checkIfNull}
            styles={styles}
            setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
        />
    ) : topicConfigContext.tab.chartSettings.chartType === 'verticalBar' ? (
        <VerticalBar
            json={currentData}
            selection={selection}
            sortByAxis={sortByAxis}
            dataUnavailable={dataUnavailable}
            setDataUnavailable={setDataUnavailable}
            handleDropdownSelect={handleDropdownSelect}
            checkIfNull={checkIfNull}
            styles={styles}
            setMissingSuppressedModalOpen={setMissingSuppressedModalOpen}
            setChartInfoModalOpen={setChartInfoModalOpen}
        />
    ) : null;
};

export default ChartContent;
